<template>
  <Popper
    v-model:show="showTasksModal"
  
    :config="popperConfig"
    :appendToBody="true"
    :trigger="'click'"
    :ignoreClickOutside="['.task-menu.click-container', '#resume-upload-modal']"
  >
    <template #trigger>
      <div
        ref="trigger"
        class="task-icon"
        :class="{ '_active' : inProgressLocal }"
      >
        <div class="inline-loader">
          <div v-if="inProgressLocal" class="loader-component" />
          <div class="loader-inner">
            <IconButton
              :icon="'import_export'"
              :showHover="false"
              :width="'32px'"
              :height="'32px'"
            />
            <span class="subtitle">Tasks</span>
          </div>
        </div>
      </div>
    </template>
    <BaseCard
      class="tasks-modal"
    >
      <template #body>
        <IconButton
          class="close-btn"
          :icon="'close'"
          :width="20"
          :height="20"
          @click="showTasksModal = false"
        />
        <div class="mode-selector">
          <input
            id="option-local"
            v-model="mode"
            type="radio"
            name="mode-selector"
            :value="'local'"
          >
          <label for="option-local">Local ({{ numLocalInProgressTasks }})</label>
          <input
            id="option-in_progress"
            v-model="mode"
            type="radio"
            name="mode-selector"
            :value="'in_progress'"
          >
          <label for="option-in_progress">In Progress ({{ numInProgressTasks }})</label>
          <input
            id="option-completed"
            v-model="mode"
            type="radio"
            name="mode-selector"
            :value="'completed'"
          >
          <label for="option-completed">Completed ({{ numCompletedTasks }})</label>
        </div>
        <div class="mt-2 tasks-box scrollbar">
          <p v-if="mode==='local'" class="disclaimer"><strong>Warning:</strong> These tasks will be terminated if you exit the website or close your browser.</p>
          <div>
            <template v-if="mode==='local'">
              <div v-for="task in localTasks" :key="task.id" class="task">
                <TaskStatusBars
                  :task="task"
                  :singleBar="true"
                  :showDate="true"
                  :showIcon="true"
                />
              </div>
            </template>
            <template v-if="mode==='in_progress'">
              <div v-for="task in inProgressTasks" :key="task.id" class="task">
                <TaskStatusBars
                  :task="task"
                  :singleBar="true"
                  :showDate="true"
                  :showIcon="true"
                />
              </div>
            </template>
            <template v-if="mode==='completed'">
              <div v-for="task in completeTasks" :key="task.id" class="task">
                <TaskStatusBars
                  :task="task"
                  :singleBar="true"
                  :showDate="true"
                  :showIcon="true"
                />
              </div>
            </template>
            <template v-if="mode==='error'">
              <div v-for="task in errorTasks" :key="task.id" class="task">
                <TaskStatusBars
                  :task="task"
                  :singleBar="true"
                  :showDate="true"
                  :showIcon="true"
                />
              </div>
            </template>
          </div>
        </div>
      </template>
    </BaseCard>
  </Popper>
</template>

<script setup>
import {
  ref, watch, nextTick,
} from 'vue';
import Popper from '@/components/Popper.vue';
import IconButton from '@/components/IconButton.vue';
import BaseCard from '@/components/BaseCard.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';
import 'vue-toast-notification/dist/theme-sugar.css';
import useTaskModal from '@/composables/useTaskModal.js';

const {
  localTasks,
  numLocalInProgressTasks,
  inProgressTasks,
  completeTasks,
  errorTasks,
  terminatedTasks,
  pauseGetTasks,
  resumeGetTasks,
  getTasks,
  isGettingTasks,
  numInProgressTasks,
  numCompletedTasks,
  inProgressLocal,
  inProgress,
  numErrorTasks,
  numTerminatedTasks,
  showTasksModal,
  mode,
} = useTaskModal();

const popperConfig = {
  placement: 'bottom-start',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [75, -15],
      },
    },
    {
      name: 'preventOverflow',
      options: {
        padding: 0,
      },
    },
    {
      name: 'flip',
      enabled: true,
    },
  ],
};

watch(inProgressTasks, async (newTasks) => {
  if (newTasks.length > 0 && !isGettingTasks.value) {
    resumeGetTasks();
  }

  if (newTasks.length === 0 && isGettingTasks.value) {
    pauseGetTasks();
    await nextTick();
    getTasks();
  }
});

watch(showTasksModal, () => {
  if (showTasksModal.value) {
    getTasks();
  }
});

</script>

<style lang="scss" scoped>
.tasks-modal {
  width: 600px;
  height: 400px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.3);
}

@media (max-width: $breakpoint-md) {
  .tasks-modal {
    width: 100%;
  }

}

.icon-container {
  display: flex;
  align-items: center;
  button + button {
    margin-left: 10px;
  }
  .icon {
    visibility: visible;
    @include themify() {
      color: themed('icon-color-inactive');
    }
  }

  .icon:hover {
    visibility: visible;
    @include themify() {
      color: themed('icon-color-primary');
    }
  }
}

.task-icon {
  position : absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 60px;
  height: 60px;
  bottom: 8px;
  right: 20px;
  border-radius: 50%;
  color: var(--color-primary);
  background: var(--color-white-500);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);

  &._active {
    color: var(--color-primary);
  }

  & button {
    pointer-events: none;
  }
}

.subtitle {
  font-size: 0.65rem;
  font-weight: bold;
}

.mode-selector {
  display: flex;
  flex-direction: row;
}

.mode-selector input[type="radio"] {
  display: none;
}

.mode-selector label {
  padding: 2px 5px;
  width: fit-content;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
}

.mode-selector input[type="radio"]:checked+label {
  @include themify() {
    color: themed('color-primary-500');
    border-bottom: 2px solid themed('color-primary-500');
  }
}

.tasks-box {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 0 1 100%;
  align-items: flex-end;
  overflow-y: scroll;

  & > div {
    display: flex;
    flex-direction: column-reverse;
    width: 100%;
  }

  .task {
    display: flex;
    flex-direction: row;
  }

  .disclaimer {
    margin: 4px 0 8px 0;
    font-weight: 600;
    font-style: italic;
    line-height: 1.15;
    font-size: 0.875rem;
    color: var(--body-text-color-secondary);
  }
}

.inline-loader {
  position: relative;
  .loader-component {
    width: 60px;
    height: 60px;
    @include themify() {
      border: 3px solid themed('loader-background');
      border-top: 3px solid themed('loader-main-color');;
    }
    border-radius: 50%;
    animation: spin 2s linear infinite;
  }

  .loader-inner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

  }
}

@keyframes spin {
 0% { transform: rotate(0deg); }
 100% { transform: rotate(360deg); }
}

.close-btn {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 4px;
  margin-left: auto;
  cursor: pointer;
  top: 16px;
  right: 16px;
}

.close-btn:hover {
  background: rgba(128, 128, 128, 0.25);
}
</style>
